import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
  Card,
  CardContent,
  TextField,
  Divider,
  useMediaQuery,
  useTheme,
  Autocomplete,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Tabs,
  Tab,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Backdrop
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  PersonAdd as PersonAddIcon,
  Description as DocIcon,
  CheckCircle as CheckIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Person as PersonIcon,
  ConfirmationNumber as TicketIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { can } from '../../utils/roleUtils';
import { getDocIcon } from '../../utils/docTypeIcons';
import ApiService from '../../services/api.service';
import { tcOrgDocumentApi } from '../../components/org-documents/orgDocumentApi';
import useOrganizationSearch from '../../hooks/useOrganizationSearch';
import { resolveExtractedDocNumber } from '../../utils/extractionUtils';
import { filesFromEvent } from '../../utils/dropPasteFiles';
import { isPrimaryDocType, isOrganizationDocType } from '../../utils/docTypeGroups';
import internalServiceService from '../../services/internal-service.service';
import EmbeddedTicketDialog from '../CreateTicket/EmbeddedTicketDialog';
import CaptureDialog from '../../components/capture/CaptureDialog';
import ServicePicker from '../../components/work-orders/ServicePicker';
import WorkOrderEmployeePicker from '../../components/work-orders/WorkOrderEmployeePicker';
import { buildServiceLine } from '../../utils/workOrderServiceLines';
import { CropFree as CaptureIcon, Inventory2Outlined as GroupChipIcon, Close as CloseIcon } from '@mui/icons-material';

// Font size constants
const fontSize = {
  xs: '0.75rem',
  sm: '0.875rem',
  md: '1rem',
};

// Document type icons — maps doc type codes to emoji

// Compact Autocomplete dropdown styling — applied to all Autocompletes
const COMPACT_LISTBOX = { sx: { '& .MuiAutocomplete-option': { fontSize: '0.8125rem', py: 0.5, minHeight: 32 } } };

// Tax Configuration - UAE VAT Rate
const VAT_RATE = 0.05;

// Create initial customer object
const createCustomer = (index = 0) => ({
  id: Date.now() + index,
  name: '',
  passportNumber: '',
  emiratesId: '',
  // Optional contact details — sent to Thaklis with the employee, not stored in Managers.
  phone: '',
  email: '',
  services: [],
  documents: [],
  // WO create: employee selection - the Thaklis employee this applicant is ({ uid, segment, name, passportNumber,
  // emiratesId }), the document types added from "Add document" (optional rows), and the types already pre-filled
  // (or tried) from the employee's documents.
  employee: null,
  extraDocTypes: [],
  _prefillTried: [],
  // Company-document rows already pre-filled (or tried) from the selected organization's documents.
  _orgPrefillTried: [],
  // Custom field values shared across this customer's services, keyed by fieldId.
  // Distributed to the correct line items on save (each item gets its own service's fields).
  fieldValues: {}
});

// Merge the custom fields of all a customer's services into one deduped list (by fieldId).
// A merged field is required if ANY service that carries it marks it required.
const mergeCustomFields = (customer) => {
  const map = new Map();
  (customer?.services || []).forEach(svc => {
    (svc.customFields || []).forEach(f => {
      const key = f.fieldId || f.code;
      if (!key) return;
      const existing = map.get(key);
      if (existing) {
        if (f.required) existing.required = true;
      } else {
        map.set(key, { ...f, fieldId: f.fieldId || f.code, required: !!f.required });
      }
    });
  });
  return Array.from(map.values());
};

// Merge the required-document rules of all a customer's services into one deduped list (by code).
// A merged doc is required if ANY service that carries it marks it required.
const mergeRequiredDocs = (customer) => {
  const map = new Map();
  (customer?.services || []).forEach(svc => {
    (svc.documentRequirements || []).forEach(r => {
      const key = r.code;
      if (!key) return;
      const existing = map.get(key);
      if (existing) {
        if (r.required) existing.required = true;
      } else {
        map.set(key, { ...r, required: !!r.required });
      }
    });
  });
  return Array.from(map.values());
};

// An on-file document whose expiry date has passed can't satisfy a required document.
const isExpiredOnFile = (group) => {
  const raw = group?.common?.expiryDate;
  if (!raw) return false;
  const exp = new Date(raw);
  if (Number.isNaN(exp.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return exp < today;
};

const CreateWorkOrder = ({ currentUser, isEditMode = false }) => {
  const navigate = useNavigate();
  const { workOrderId } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Thaklis-features toggle: when disabled, no tickets / no employee+doc sync to Thaklis / no
  // identifier warnings (services are already internal-only) and no walk-in customer option.
  const thaklisDisabled = currentUser?.thaklisEnabled === false;
  const canCreateTicket = can(currentUser, 'thaklis_features', 'write');

  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(isEditMode);

  // Form states
  const [customerType, setCustomerType] = useState('organization');
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const {
    options: organizations,
    loading: loadingOrgs,
    onInputChange: onOrganizationInputChange,
    filterOptions: organizationFilterOptions
  } = useOrganizationSearch({
    typingCenterId: currentUser?.typingCenterId || currentUser?.typing_center_id,
    mapOption: (org) => ({
      id: org.id,
      name: org.name || org.organization_name,
      code: org.code || org.organization_code || '',
      email: org.email || '',
      phone: org.phone || org.contact_number || '',
      type: 'internal',
      // CLIENT / TYPING_CENTER — decides whether this customer gets Thaklis employee/doc tracking.
      orgType: org.type || null
    })
  });

  // Mapped Thaklis organization (which Thaklis org the applicants belong to) —
  // same picker as the invoice-create page; always shown, both customer types.
  const [mappedOrganizations, setMappedOrganizations] = useState([]);
  const [selectedMappedOrg, setSelectedMappedOrg] = useState(null);
  const [loadingMappedOrgs, setLoadingMappedOrgs] = useState(false);

  // Confirm dialog when some applicants have no unique identifier
  // Changing the organization invalidates prices, field defaults, and available services,
  // so it clears all entered customers/services/documents — confirm first when data would be lost.
  const [orgChangeConfirmOpen, setOrgChangeConfirmOpen] = useState(false);
  const [pendingOrgChange, setPendingOrgChange] = useState(null);
  // Set when the confirmation is for a customer-type switch (walk-in ↔ organization) rather than an
  // organization change; both wipe entered data because the available services differ.
  const [pendingCustomerType, setPendingCustomerType] = useState(null);
  // Document requirements warning dialog
  const [docWarningOpen, setDocWarningOpen] = useState(false);
  const [captureTarget, setCaptureTarget] = useState(null); // { code, label } of the doc type being captured
  const [missingDocs, setMissingDocs] = useState([]); // [{customerName, serviceName, missingDocs: [{label, required}]}]
  // Sync progress feedback
  const [syncing, setSyncing] = useState(false);
  const [orderDate, setOrderDate] = useState(new Date().toISOString().split('T')[0]);
  const [expectedCompletionDate, setExpectedCompletionDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() + 7);
    return date.toISOString().split('T')[0];
  });
  const [notes, setNotes] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  // Contact info
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');

  // Tab-based customers (each customer has their own services and documents)
  const [customers, setCustomers] = useState([createCustomer(0)]);
  const [activeTab, setActiveTab] = useState(0);

  // Queue of uploaded docs whose OCR-detected name differs from the customer already on
  // that tab. Each entry drives a "detected applicant is different" prompt (head of queue).
  const [pendingMismatches, setPendingMismatches] = useState([]);

  // Available services and document types
  const [availableServices, setAvailableServices] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);

  // KAN-29: whether Thaklis services are offered on work orders for this TC (Work Order Settings —
  // default off). When off, only internal services are selectable. When on, internal services are on
  // the first tab and Thaklis services on the second. Also gated by the TC's Thaklis-features toggle.
  const [thaklisServicesEnabled, setThaklisServicesEnabled] = useState(false);
  const [serviceTab, setServiceTab] = useState(0);
  const showThaklisServices = !thaklisDisabled && thaklisServicesEnabled;

  // Per-customer form state
  // Service groups (Service Catalog → Service Groups) offered on the Service | Group switch
  const [serviceGroups, setServiceGroups] = useState([]);
  const [selectedDocType, setSelectedDocType] = useState('');
  // Which document row a file is being dragged over (doc code, '__extra', or null).
  const [dragTarget, setDragTarget] = useState(null);

  // In-place ticket creation from a Thaklis service line (create mode only)
  const [ticketDialog, setTicketDialog] = useState({
    open: false, serviceItem: null, customerId: null, prefillName: ''
  });

  // Summary sidebar expanded state
  const [summaryExpanded, setSummaryExpanded] = useState(true);

  // Fetch data on mount
  useEffect(() => {
    if (currentUser) {
      fetchMappedOrganizations();
      fetchDocumentTypes();
      fetchWorkOrderConfig();
      internalServiceService.getServiceGroups().then(setServiceGroups).catch(() => setServiceGroups([]));
    }
  }, [currentUser]);

  // (Re)load the addable services whenever the customer/org changes — internal-service prices are
  // resolved for the selected organization (walk-in / no org → uniform services only).
  useEffect(() => {
    if (currentUser) {
      fetchServices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, customerType, selectedOrganization]);

  // Fetch work order in edit mode
  useEffect(() => {
    if (isEditMode && workOrderId && organizations.length > 0) {
      fetchWorkOrder();
    }
  }, [isEditMode, workOrderId, organizations]);

  const fetchWorkOrder = async () => {
    setLoading(true);
    try {
      const response = await ApiService.getWorkOrderById(workOrderId);
      const workOrder = response?.data?.data || response?.data;

      if (workOrder) {
        const orgId = workOrder.organization_id || workOrder.organizationId;
        const org = organizations.find(o => o.id === orgId);
        if (org) setSelectedOrganization(org);

        const mappedOrgId = workOrder.mapped_organization_id || workOrder.mappedOrganizationId;
        const mappedOrgName = workOrder.mapped_organization_name || workOrder.mappedOrganizationName;
        if (mappedOrgId) {
          setSelectedMappedOrg(prev => prev && prev.id === mappedOrgId
            ? prev
            : { id: mappedOrgId, name: mappedOrgName || '', code: '' });
        }

        setOrderDate(workOrder.order_date || workOrder.orderDate || new Date().toISOString().split('T')[0]);
        setExpectedCompletionDate(workOrder.expected_completion_date || workOrder.expectedCompletionDate || '');
        setNotes(workOrder.notes || '');
        setTitle(workOrder.title || '');
        setDescription(workOrder.description || '');

        const hasOrganization = workOrder.organization_id || workOrder.organizationId;
        setCustomerType(hasOrganization ? 'organization' : 'walkin');
        setCustomerEmail(workOrder.customer_email || workOrder.customerEmail || '');
        setCustomerPhone(workOrder.customer_phone || workOrder.customerPhone || '');

        // Load customers with their services and documents
        const woCustomers = workOrder.customers || [];
        const woLineItems = workOrder.line_items || workOrder.lineItems || [];
        const woDocuments = workOrder.documents || [];

        if (woCustomers.length > 0) {
          const loadedCustomers = woCustomers.map((c, idx) => ({
            id: c.id || Date.now() + idx,
            name: c.name || c.customer_name || '',
            passportNumber: c.passportNumber || c.passport_number || '',
            emiratesId: c.emiratesId || c.emirates_id || '',
            services: woLineItems.filter(item => item.customer_id === c.id || item.customerId === c.id).map((item, i) => ({
              id: item.id || `temp-${Date.now()}-${i}`,
              service_id: item.service_id || item.serviceId,
              service_name: item.service_name || item.serviceName,
              service_description: item.service_description || item.serviceDescription || '',
              quantity: item.quantity || 1,
              unit_price: item.unit_price || item.unitPrice || 0,
              service_charge: item.service_charge || item.serviceCharge || 0,
              govt_charge: item.govt_charge || item.govtCharge || 0,
              tax_value: item.tax_value || item.taxValue || 0,
              line_total: item.line_total || item.lineTotal || 0,
              markup_amount: item.markup_amount || item.markupAmount || 0,
              is_custom_service: item.is_custom_service || item.isCustomService || false,
              service_type: item.service_type || item.serviceType || 'INTERNAL'
            })),
            documents: woDocuments.filter(d => d.customer_id === c.id || d.customerId === c.id).map(d => ({
              id: d.id || Date.now(),
              fileName: d.file_name || d.fileName,
              fileSize: d.file_size || d.fileSize || 0,
              documentType: d.document_type || d.documentType,
              documentTypeName: d.document_type_name || d.documentTypeName || d.document_type
            }))
          }));
          setCustomers(loadedCustomers);
        } else if (woLineItems.length > 0) {
          // No customers[] array on the response — reconstruct from the
          // per-line-item customer_name / customer_id_number. Line items with
          // the same customer_name collapse into one customer record.
          const byName = new Map();
          woLineItems.forEach((item, i) => {
            const name = item.customer_name || item.customerName || '';
            if (!byName.has(name)) {
              byName.set(name, {
                id: Date.now() + byName.size,
                name,
                passportNumber: item.customer_id_number || item.customerIdNumber || '',
                services: [],
                documents: []
              });
            }
            byName.get(name).services.push({
              id: item.id || `temp-${Date.now()}-${i}`,
              service_id: item.service_id || item.serviceId,
              service_name: item.service_name || item.serviceName,
              service_description: item.service_description || item.serviceDescription || '',
              quantity: item.quantity || 1,
              service_charge: item.service_charge || item.serviceCharge || 0,
              govt_charge: item.govt_charge || item.govtCharge || 0,
              tax_value: item.tax_value || item.taxValue || 0,
              line_total: item.line_total || item.lineTotal || 0,
              markup_amount: item.markup_amount || item.markupAmount || 0,
              service_type: item.service_type || item.serviceType || 'INTERNAL'
            });
          });
          // Attach documents by customer_name
          woDocuments.forEach(d => {
            const name = d.customer_name || d.customerName || '';
            if (byName.has(name)) {
              byName.get(name).documents.push({
                id: d.id || Date.now(),
                fileName: d.file_name || d.fileName,
                fileSize: d.file_size || d.fileSize || 0,
                documentType: d.document_type || d.documentType,
                documentTypeName: d.document_type_name || d.documentTypeName || d.document_type
              });
            }
          });
          if (byName.size > 0) {
            setCustomers(Array.from(byName.values()));
          }
        }
      }
    } catch (err) {
      console.error('Error fetching work order:', err);
      setError('Failed to load work order');
    } finally {
      setLoading(false);
    }
  };


  const fetchMappedOrganizations = async () => {
    setLoadingMappedOrgs(true);
    try {
      const typingCenterId = currentUser?.typingCenterId || currentUser?.typing_center_id;
      const response = await ApiService.getMappings(typingCenterId);
      const orgs = (response?.data?.data || []).map(m => ({
        id: m.organization_id,
        name: m.organization_name,
        code: m.organization_code || '',
        // mapping id (typing_center_organization_mapping.id) — needed to init the
        // Thaklis client for in-place ticket creation from a WO line.
        mappingId: m.id || m.mappingId
      }));
      setMappedOrganizations(orgs);
      // Auto-select when exactly one mapping (mirrors invoice-create behavior).
      if (orgs.length === 1) {
        setSelectedMappedOrg(orgs[0]);
      }
    } catch (err) {
      console.error('Error fetching mapped organizations:', err);
    } finally {
      setLoadingMappedOrgs(false);
    }
  };

  const fetchServices = async () => {
    try {
      // Resolve internal-service prices for the selected org; walk-in / no org → uniform services only.
      const orgId = customerType === 'organization' ? (selectedOrganization?.id || null) : null;
      const allServices = await internalServiceService.getServicesForLineItems(orgId);
      const services = allServices.map(s => ({
        id: s.id,
        name: s.name || s.service_name,
        description: s.description || '',
        service_charge: s.service_charge || s.serviceCharge || 0,
        tax_on_service_charge: s.tax_on_service_charge || s.taxOnServiceCharge || 0,
        govt_charge: s.govt_charge || s.govtCharge || 0,
        type: s.type || 'THAKLIS',
        createsTask: s.createsTask || s.creates_task || false,
        // Carry the resolved custom fields + required documents so they reach the line item.
        customFields: s.customFields || s.custom_fields || [],
        documentRequirements: s.documentRequirements || s.document_requirements || []
      }));
      setAvailableServices(services);
    } catch (err) {
      console.error('Error fetching services:', err);
      setAvailableServices([]);
    }
  };

  const fetchWorkOrderConfig = async () => {
    try {
      const tcId = currentUser?.typingCenterId || currentUser?.typing_center_id;
      if (!tcId) return;
      const resp = await ApiService.getWorkOrderConfig(tcId);
      const c = resp?.data?.data || resp?.data || {};
      setThaklisServicesEnabled(c.thaklis_services_enabled === true);
    } catch (err) {
      // Default to off (internal services only) if the config can't be read.
      setThaklisServicesEnabled(false);
    }
  };

  const fetchDocumentTypes = async () => {
    try {
      // Use TC's own document types (categorized) — falls back to Thaklis API
      const tcDocTypes = await import('../../services/tc-document-type.service').then(m => m.default.getActive()).catch(() => []);
      if (tcDocTypes.length > 0) {
        // Sort: Identity first, then Immigration, Medical, Employment, Company, Financial, Other
        const categoryOrder = ['Identity', 'Immigration', 'Medical', 'Employment', 'Company', 'Financial', 'Legal', 'Other'];
        const sorted = [...tcDocTypes].sort((a, b) => {
          const ai = categoryOrder.indexOf(a.category || 'Other');
          const bi = categoryOrder.indexOf(b.category || 'Other');
          if (ai !== bi) return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
          return (a.sortOrder || 0) - (b.sortOrder || 0);
        });
        setDocumentTypes(sorted.map(dt => ({ code: dt.code, label: dt.label, category: dt.category, holderType: dt.holder_type || dt.holderType, isPrimary: isPrimaryDocType(dt) })));
      } else {
        const response = await ApiService.getDocumentTypes();
        setDocumentTypes(response.data?.data || []);
      }
    } catch (err) {
      console.error('Error fetching document types:', err);
      setDocumentTypes([]);
    }
  };

  // Tab management
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // Reset per-tab form state
    setSelectedDocType('');
  };

  const handleAddCustomer = () => {
    const newCustomer = createCustomer(customers.length);
    setCustomers(prev => [...prev, newCustomer]);
    setActiveTab(customers.length); // Switch to new tab
  };

  const handleRemoveCustomer = (customerId) => {
    if (customers.length === 1) {
      setError('At least one customer is required');
      return;
    }
    const customerIndex = customers.findIndex(c => c.id === customerId);
    setCustomers(prev => prev.filter(c => c.id !== customerId));
    // Adjust active tab if needed
    if (activeTab >= customers.length - 1) {
      setActiveTab(Math.max(0, customers.length - 2));
    } else if (activeTab > customerIndex) {
      setActiveTab(activeTab - 1);
    }
  };

  const handleCustomerChange = (customerId, field, value) => {
    setCustomers(prev => prev.map(c => {
      if (c.id !== customerId) return c;
      return { ...c, [field]: value };
    }));
  };

  // Apply an organization selection: set the org + its contact details, and reset all entered
  // customers/services/documents/field values (prices, field defaults and the available-service
  // list are all org-specific, so anything entered for the previous org no longer applies).
  const applyOrganizationChange = (value) => {
    setSelectedOrganization(value);
    if (value) {
      setCustomerEmail(value.email || '');
      setCustomerPhone(value.phone || '');
    } else {
      setCustomerEmail('');
      setCustomerPhone('');
    }
    setCustomers([createCustomer(0)]);
    setActiveTab(0);
    setSelectedDocType('');
  };

  // Organization change (from the Autocomplete). Edit mode keeps the old behavior (services are
  // managed on the detail page). In create mode, confirm before wiping anything already entered.
  const handleOrganizationChange = (event, value) => {
    if (isEditMode) {
      setSelectedOrganization(value);
      if (value) { setCustomerEmail(value.email || ''); setCustomerPhone(value.phone || ''); }
      else { setCustomerEmail(''); setCustomerPhone(''); }
      return;
    }
    const changing = (value?.id || null) !== (selectedOrganization?.id || null);
    const hasEntries = customers.some(c => (c.services?.length || 0) > 0 || (c.documents?.length || 0) > 0);
    if (changing && hasEntries) {
      setPendingOrgChange(value);
      setOrgChangeConfirmOpen(true);
      return;
    }
    applyOrganizationChange(value);
  };

  const confirmOrgChange = () => {
    if (pendingCustomerType) {
      applyCustomerTypeChange(pendingCustomerType);
      setPendingCustomerType(null);
    } else {
      applyOrganizationChange(pendingOrgChange);
      setPendingOrgChange(null);
    }
    setOrgChangeConfirmOpen(false);
  };

  const cancelOrgChange = () => {
    setPendingOrgChange(null);
    setPendingCustomerType(null);
    setOrgChangeConfirmOpen(false);
  };

  // Apply a customer-type switch: clear the org + contacts and reset all entered customers/services/
  // documents/field values (walk-in vs organization changes which services are available and their prices).
  const applyCustomerTypeChange = (type) => {
    setCustomerType(type);
    setSelectedOrganization(null);
    setCustomerEmail('');
    setCustomerPhone('');
    setCustomers([createCustomer(0)]);
    setActiveTab(0);
    setSelectedDocType('');
  };

  const handleCustomerTypeChange = (type) => {
    if (type === customerType) return;
    if (isEditMode) { setCustomerType(type); return; }
    const hasEntries = customers.some(c => (c.services?.length || 0) > 0 || (c.documents?.length || 0) > 0);
    if (hasEntries) {
      setPendingCustomerType(type);
      setOrgChangeConfirmOpen(true);
      return;
    }
    applyCustomerTypeChange(type);
  };

  // Service management - add lines (one service, or the ticked services of a group) to the current customer
  const addLinesToCustomer = (lines) => {
    const currentCustomer = customers[activeTab];
    setCustomers(prev => prev.map(c => {
      if (c.id !== currentCustomer.id) return c;
      // Seed this customer's shared field values from the org defaults on the added services —
      // only for fields the user hasn't already filled (never overwrite a typed value).
      const nextFieldValues = { ...(c.fieldValues || {}) };
      lines.forEach(line => line.customFields.forEach(f => {
        const def = f.orgDefault;
        const current = nextFieldValues[f.fieldId];
        if (def != null && String(def).trim() !== '' && (current == null || String(current).trim() === '')) {
          nextFieldValues[f.fieldId] = def;
        }
      }));
      return { ...c, services: [...c.services, ...lines], fieldValues: nextFieldValues };
    }));
  };

  const handleAddServiceToCustomer = (service, markup) => addLinesToCustomer([buildServiceLine(service, markup)]);

  const handleAddGroupToCustomer = (group, services) =>
    addLinesToCustomer(services.map(svc => buildServiceLine(svc, 0, group)));

  const handleRemoveGroupFromCustomer = (groupId) => {
    const currentCustomer = customers[activeTab];
    setCustomers(prev => prev.map(c =>
      c.id === currentCustomer.id ? { ...c, services: c.services.filter(s => s.group_id !== groupId) } : c
    ));
  };

  // Custom field values are captured once per customer (keyed by fieldId), not per service —
  // the same field shared by several services is entered a single time.
  const handleFieldValueChange = (fieldId, value) => {
    const currentCustomer = customers[activeTab];
    setCustomers(prev => prev.map(c =>
      c.id === currentCustomer.id
        ? { ...c, fieldValues: { ...(c.fieldValues || {}), [fieldId]: value } }
        : c
    ));
  };

  const handleRemoveServiceFromCustomer = (serviceId) => {
    const currentCustomer = customers[activeTab];
    setCustomers(prev => prev.map(c =>
      c.id === currentCustomer.id
        ? { ...c, services: c.services.filter(s => s.id !== serviceId) }
        : c
    ));
  };

  // --- In-place ticket creation for Thaklis service lines -------------------
  const handleOpenTicketDialog = (item) => {
    if (!selectedMappedOrg?.mappingId) {
      setError('Select the mapped organization before creating a ticket.');
      return;
    }
    const cust = customers[activeTab];
    setTicketDialog({
      open: true,
      serviceItem: item,
      customerId: cust.id,
      prefillName: cust.name?.trim() || ''
    });
  };

  const handleCloseTicketDialog = () => {
    setTicketDialog({ open: false, serviceItem: null, customerId: null, prefillName: '' });
  };

  // The embedded ticket form hands back the created ticket. Re-fetch it through the
  // managers ticket list (same shape the manual "Link Ticket" flow uses) so the stored
  // snapshot carries charges/customerName for the later WO→invoice conversion, then
  // stamp it onto the line item so submit can auto-link it.
  const handleTicketCreated = async (createdTicket) => {
    const { serviceItem, customerId } = ticketDialog;
    if (!serviceItem) { handleCloseTicketDialog(); return; }

    const ticketId = createdTicket?.id || createdTicket?.data?.id;
    let snapshot = createdTicket?.data || createdTicket;
    let ticketNumber = createdTicket?.orgTicketNumber || createdTicket?.org_ticket_number
      || createdTicket?.ticketNumber || createdTicket?.ticket_number
      || createdTicket?.data?.ticketNumber;

    try {
      const typingCenterId = currentUser?.typingCenterId || currentUser?.typing_center_id;
      if (typingCenterId && ticketId && serviceItem.service_id) {
        const resp = await ApiService.getTickets({ typingCenterId, serviceId: serviceItem.service_id });
        const list = resp?.data?.data || resp?.data || [];
        const match = list.find(t => String(t.id) === String(ticketId));
        if (match) {
          snapshot = match;
          ticketNumber = match.orgTicketNumber || match.org_ticket_number
            || match.ticketNumber || match.ticket_number || ticketNumber;
        }
      }
    } catch (err) {
      console.error('Could not re-fetch created ticket for snapshot, using create payload:', err);
    }

    setCustomers(prev => prev.map(c =>
      c.id === customerId
        ? {
            ...c,
            services: c.services.map(s => s.id === serviceItem.id
              ? { ...s, created_ticket: { id: ticketId, number: ticketNumber, snapshot } }
              : s)
          }
        : c
    ));
    setSuccess(`Ticket ${ticketNumber || ''} created — it will be linked when the work order is created.`);
    handleCloseTicketDialog();
  };

  const handleRemoveCreatedTicket = (customerId, serviceItemId) => {
    setCustomers(prev => prev.map(c =>
      c.id === customerId
        ? { ...c, services: c.services.map(s => s.id === serviceItemId ? { ...s, created_ticket: null } : s) }
        : c
    ));
  };

  // Document management — supports selecting multiple files at once. Each file is
  // staged as its own document (same type) and OCR-extracted individually; on sync,
  // files of the same document type are attached together in one bulk batch.
  // `forced` ({ code, label }) is passed by a required-document slot so the upload targets that
  // specific doc type; without it the upload uses the "add another document" type dropdown.
  const handleFileUpload = async (event, forced = null) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    const useDocType = forced?.code || selectedDocType;
    if (!useDocType) {
      setError('Please select a document type');
      return;
    }

    const docType = documentTypes.find(d => (d.code || d.id) === useDocType);
    const currentCustomer = customers[activeTab];
    const baseId = Date.now();
    // A company document is not read by Thaklis' person OCR (it has no applicant, and a "licence" is taken for a
    // driving licence); the backend reads it when it is stored in the customer's company documents.
    const companyDoc = isOrganizationDocType(documentTypes, useDocType);

    const stagedDocs = files.map((file, i) => ({
      id: baseId + i,
      file,
      fileName: file.name,
      fileSize: file.size,
      documentType: useDocType,
      documentTypeName: forced?.label || docType?.label || docType?.name || useDocType,
      extracting: !companyDoc,
      // Extracted tracking metadata (filled after OCR)
      documentNumber: null,
      issueDate: null,
      expiryDate: null
    }));

    setCustomers(prev => prev.map(c =>
      c.id === currentCustomer.id
        ? { ...c, documents: [...c.documents, ...stagedDocs] }
        : c
    ));

    if (!forced) setSelectedDocType('');
    if (event.target) event.target.value = '';

    const clean = (v) => {
      if (v === null || v === undefined) return '';
      const s = String(v).trim();
      return (!s || s.toLowerCase() === 'null') ? '' : s;
    };

    if (companyDoc) return;

    // OCR extraction (generalized prompt) per file: auto-fill applicant identity when
    // present (only into empty fields) and stash each document's own number/issue/expiry.
    await Promise.all(stagedDocs.map(async (staged) => {
      try {
        const response = await ApiService.extractDocument(staged.file, 'GENERAL', staged.documentType);
        const extracted = response?.data?.data || {};
        // Surface a clear reason when extraction didn't run (e.g. the TC's OpenAI key is not
        // configured, or Thaklis skipped it) instead of silently filling nothing. The document is
        // still staged either way — extraction is best-effort.
        const extractFailure = response?.data?.success === false
          ? (response.data.error || response.data.message)
          : (extracted._extractionSkipped
            ? (extracted._extractionSkippedReason || 'Automatic field extraction was skipped — enter the fields manually.')
            : null);
        if (extractFailure) {
          setError(extractFailure);
        }
        const fullName = clean(extracted.fullName);
        const passportNumber = clean(extracted.passportNumber);
        const emiratesIdNumber = clean(extracted.emiratesIdNumber);
        const phoneNumber = clean(extracted.phoneNumber);
        const email = clean(extracted.email);
        const documentNumber = resolveExtractedDocNumber(extracted, staged.documentType);
        const issueDate = clean(extracted.issueDate);
        const expiryDate = clean(extracted.expiryDate);

        let mismatch = null;
        setCustomers(prev => prev.map(c => {
          if (c.id !== currentCustomer.id) return c;
          const existingName = c.name?.trim() || '';
          // A name was detected that differs from the customer already on this tab —
          // don't touch the identity; queue a prompt asking whether to split it out.
          if (existingName && fullName && existingName.toLowerCase() !== fullName.toLowerCase()) {
            mismatch = {
              docId: staged.id,
              sourceCustomerId: c.id,
              existingName,
              extracted: {
                fullName,
                passportNumber,
                emiratesId: emiratesIdNumber,
                phone: phoneNumber,
                email
              }
            };
            // Clear the spinner + record this doc's own metadata; leave identity as-is.
            return {
              ...c,
              documents: c.documents.map(d => d.id === staged.id
                ? { ...d, extracting: false, documentNumber, issueDate, expiryDate }
                : d)
            };
          }
          return {
            ...c,
            // Only fill fields that are currently empty (never overwrite user input)
            name: existingName ? c.name : (fullName || c.name),
            passportNumber: c.passportNumber?.trim() ? c.passportNumber : (passportNumber || c.passportNumber),
            emiratesId: c.emiratesId?.trim() ? c.emiratesId : (emiratesIdNumber || c.emiratesId),
            phone: c.phone?.trim() ? c.phone : (phoneNumber || c.phone),
            email: c.email?.trim() ? c.email : (email || c.email),
            documents: c.documents.map(d => d.id === staged.id
              ? { ...d, extracting: false, documentNumber, issueDate, expiryDate }
              : d)
          };
        }));
        if (mismatch) {
          setPendingMismatches(prev =>
            prev.some(m => m.docId === mismatch.docId) ? prev : [...prev, mismatch]);
        }
      } catch (extractErr) {
        console.error('Document extraction failed:', extractErr);
        setError('Document extraction failed — enter the fields manually.');
        // Extraction is best-effort — clear the spinner and keep the staged file.
        setCustomers(prev => prev.map(c =>
          c.id === currentCustomer.id
            ? { ...c, documents: c.documents.map(d => d.id === staged.id ? { ...d, extracting: false } : d) }
            : c
        ));
      }
    }));
  };

  // Stage documents dragged in or pasted (Ctrl/Cmd+V of a copied image) — no download needed.
  // Runs through the same staging + OCR path as a selected file (via a synthetic input event).
  const handleAddDroppedFiles = async (e, forced = null) => {
    const files = filesFromEvent(e);
    if (!files.length) return;
    e.preventDefault();
    if (!forced && !selectedDocType) {
      setError('Please select a document type');
      return;
    }
    await handleFileUpload({ target: { files, value: '' } }, forced);
  };

  const handleRemoveDocument = (docId) => {
    const currentCustomer = customers[activeTab];
    setCustomers(prev => prev.map(c =>
      c.id === currentCustomer.id
        ? { ...c, documents: c.documents.filter(d => d.id !== docId) }
        : c
    ));
  };

  // Name-mismatch prompt — user chose to keep the doc with the existing customer.
  const handleKeepSameCustomer = () => {
    setPendingMismatches(prev => prev.slice(1));
  };

  // Name-mismatch prompt — user chose to split the detected applicant into a new
  // customer: create a tab, fill it with the extracted identity, move the doc there.
  const handleMoveToNewCustomer = () => {
    const mismatch = pendingMismatches[0];
    if (!mismatch) return;

    const newIndex = customers.length;
    const newCustomer = {
      ...createCustomer(newIndex),
      name: mismatch.extracted.fullName || '',
      passportNumber: mismatch.extracted.passportNumber || '',
      emiratesId: mismatch.extracted.emiratesId || '',
      phone: mismatch.extracted.phone || '',
      email: mismatch.extracted.email || ''
    };

    setCustomers(prev => {
      let movedDoc = null;
      const updated = prev.map(c => {
        if (c.id !== mismatch.sourceCustomerId) return c;
        movedDoc = c.documents.find(d => d.id === mismatch.docId) || null;
        return { ...c, documents: c.documents.filter(d => d.id !== mismatch.docId) };
      });
      return [...updated, { ...newCustomer, documents: movedDoc ? [movedDoc] : [] }];
    });

    setActiveTab(newIndex);
    setPendingMismatches(prev => prev.slice(1));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Calculate totals per customer
  const calculateCustomerTotal = useCallback((customer) => {
    // subtotal from components (sc + gc + markup), not from line_total (which includes tax)
    const subtotal = customer.services.reduce((sum, s) =>
      sum + (s.service_charge || 0) + (s.govt_charge || 0) + (s.markup_amount || 0), 0);
    const taxTotal = customer.services.reduce((sum, s) =>
      sum + (s.tax_value || 0) + (s.tax_on_markup || 0), 0);
    const grandTotal = subtotal + taxTotal;
    return { subtotal, taxTotal, grandTotal };
  }, []);

  // Calculate grand totals across all customers
  const calculateGrandTotals = useCallback(() => {
    let grandTotal = 0;
    let taxTotal = 0;
    let subtotal = 0;

    customers.forEach(customer => {
      const totals = calculateCustomerTotal(customer);
      grandTotal += totals.grandTotal;
      taxTotal += totals.taxTotal;
      subtotal += totals.subtotal;
    });

    return { subtotal, taxTotal, grandTotal };
  }, [customers, calculateCustomerTotal]);

  // Check if customer is complete (has name and at least one service)
  const isCustomerComplete = useCallback((customer) => {
    return customer.name.trim() && customer.services.length > 0;
  }, []);

  // Employee/document tracking applies to walk-ins and CLIENT-type organization customers only —
  // never a B2B provider's typing-center customers, even when Thaklis features are enabled. Old rows
  // with no stored type fall back to the parent TC (a non-B2B TC's customers are clients).
  const isEmployeeTrackingActive = () => {
    if (thaklisDisabled) return false;
    if (customerType !== 'organization') return true;
    if (!selectedOrganization) return true;
    return selectedOrganization.orgType
      ? selectedOrganization.orgType === 'CLIENT'
      : currentUser?.organizationType !== 'B2B';
  };

  // WO create: employee selection. The customer whose employees the tab searches and creates: the selected client
  // organization, or the TC's walk-ins. Employee mode (pick or Quick add first) only while tracking is active.
  const customerKey = customerType === 'organization' ? (selectedOrganization?.id || null) : 'walkin';
  const employeeMode = !isEditMode && isEmployeeTrackingActive() && !!customerKey;

  // The document rows of a customer: the services' required documents, then the types added from "Add document"
  // as optional rows.
  const docRows = (customer) => {
    const merged = mergeRequiredDocs(customer);
    const codes = new Set(merged.map(d => d.code));
    const extras = (customer?.extraDocTypes || []).filter(code => !codes.has(code)).map(code => {
      const dt = documentTypes.find(d => (d.code || d.id) === code);
      return { code, label: dt?.label || dt?.name || code, required: false };
    });
    return [...merged, ...extras];
  };

  const addExtraDocType = (code) => {
    if (!code) return;
    const cur = customers[activeTab];
    setCustomers(prev => prev.map(c => c.id === cur.id && !(c.extraDocTypes || []).includes(code)
      ? { ...c, extraDocTypes: [...(c.extraDocTypes || []), code] } : c));
    setSelectedDocType('');
  };

  // Picking an employee fills the applicant and loads the employee's documents; files pre-filled from the previous
  // employee go.
  const selectEmployee = (customerId, emp) => {
    // One employee per applicant tab: the same person's services go on their existing tab.
    const otherIndex = customers.findIndex(c => c.id !== customerId && c.employee?.uid && c.employee.uid === emp?.uid);
    if (otherIndex >= 0) {
      const other = customers[otherIndex];
      setError(`${emp.name || other.name || 'This employee'} is already on ${other.name?.trim() || `Customer ${otherIndex + 1}`}`
        + ' - add the services there.');
      return;
    }
    setCustomers(prev => prev.map(c => c.id !== customerId ? c : {
      ...c,
      employee: emp,
      name: emp.name || c.name,
      passportNumber: emp.passportNumber || '',
      emiratesId: emp.emiratesId || '',
      _existingDocs: [],
      _prefillTried: [],
      documents: (c.documents || []).filter(d => !d._fromEmployee),
    }));
    if (!emp?.uid || !customerKey) return;
    ApiService.getCustomerEmployeeDocuments(customerKey, emp.uid)
      .then((res) => {
        const data = res?.data?.data;
        const groups = Array.isArray(data) ? data : (data?.groups || data?.documentGroups || []);
        setCustomers(prev => prev.map(c => c.id === customerId && c.employee?.uid === emp.uid
          ? { ...c, _existingDocs: groups } : c));
      })
      .catch(() => {});
  };

  const clearEmployee = (customerId) => {
    setCustomers(prev => prev.map(c => c.id !== customerId ? c : {
      ...c, employee: null, name: '', passportNumber: '', emiratesId: '', _existingDocs: [], _prefillTried: [],
      documents: (c.documents || []).filter(d => !d._fromEmployee),
    }));
  };

  // Pre-fill each document row from the employee's current document of that type: the file is fetched
  // and staged like an upload, marked _fromEmployee so it is attached to the work order but not sent to Thaklis again.
  // A type is tried once per employee, so a pre-filled file the user removes stays removed.
  useEffect(() => {
    if (!employeeMode) return;
    const jobs = [];
    customers.forEach(c => {
      if (!c.employee?.uid || !(c._existingDocs || []).length) return;
      const tried = new Set(c._prefillTried || []);
      docRows(c).forEach(row => {
        if (!row.code || tried.has(row.code)) return;
        if (isOrganizationDocType(documentTypes, row.code)) return; // company documents come from the organization
        if ((c.documents || []).some(d => d.documentType === row.code)) return;
        // The employee's current version of the type, whatever its expiry (it is uploaded to the WO as it is)
        const ofType = c._existingDocs.filter(g => (g.common?.documentType || '').toUpperCase() === row.code.toUpperCase());
        const group = ofType.find(g => g.common?.isCurrent) || ofType[0];
        jobs.push({ customerId: c.id, uid: c.employee.uid, code: row.code, label: row.label, group, file: group?.documents?.[0] });
      });
    });
    if (jobs.length === 0) return;
    setCustomers(prev => prev.map(c => {
      const mine = jobs.filter(j => j.customerId === c.id).map(j => j.code);
      return mine.length ? { ...c, _prefillTried: [...(c._prefillTried || []), ...mine] } : c;
    }));
    jobs.filter(j => j.group && !j.file?.filePath).forEach(j =>
      console.warn(`[WO prefill] ${j.code}: the employee's document has no file path, not attached`, j.group));
    jobs.filter(j => j.file?.filePath).forEach(j => {
      ApiService.getCustomerEmployeeFile(customerKey, j.file.filePath)
        .then((res) => {
          const blob = res.data;
          const name = j.file.fileName || 'document';
          const file = new File([blob], name, { type: blob?.type || j.file.contentType || 'application/octet-stream' });
          const doc = {
            id: Date.now() + Math.random(),
            file,
            fileName: name,
            fileSize: file.size,
            documentType: j.code,
            documentTypeName: j.label,
            extracting: false,
            documentNumber: j.group.common?.documentNumber || null,
            issueDate: j.group.common?.issueDate || null,
            expiryDate: j.group.common?.expiryDate || null,
            _fromEmployee: true,
          };
          setCustomers(prev => prev.map(c => c.id === j.customerId && c.employee?.uid === j.uid
            && !(c.documents || []).some(d => d.documentType === j.code)
            ? { ...c, documents: [...c.documents, doc] } : c));
        })
        .catch((err) => {
          console.error(`[WO prefill] ${j.code}: the employee's file could not be fetched`, err);
          setError(prev => prev || `The employee's ${j.label || j.code} could not be attached - upload it on the row.`);
        });
    });
    // docRows reads documentTypes; the effect re-runs on every customer change and settles once all rows were tried
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers, employeeMode, customerKey, documentTypes]);

  // Company documents: the selected organization's own documents (a client's), for pre-filling ORGANIZATION rows.
  const [orgDocGroups, setOrgDocGroups] = useState([]);
  const orgDocsOrgId = !isEditMode && customerType === 'organization' ? (selectedOrganization?.id || null) : null;
  useEffect(() => {
    setOrgDocGroups([]);
    if (!orgDocsOrgId) return undefined;
    let live = true;
    tcOrgDocumentApi(orgDocsOrgId).documents()
      .then(({ groups }) => { if (live) setOrgDocGroups(groups || []); })
      .catch(() => {}); // not a client, or no access to company documents: nothing to pre-fill
    return () => { live = false; };
  }, [orgDocsOrgId]);

  // Pre-fill each company-document row from the organization's current version of that type: the file is staged with
  // its S3 key (_orgDocKey), so at create the work order reuses that file instead of uploading it again. Tried once
  // per row, so a pre-filled file the user removes stays removed (an upload then goes to the org's documents first).
  useEffect(() => {
    if (!orgDocsOrgId || orgDocGroups.length === 0) return;
    const jobs = [];
    customers.forEach(c => {
      const tried = new Set(c._orgPrefillTried || []);
      docRows(c).forEach(row => {
        if (!row.code || tried.has(row.code) || !isOrganizationDocType(documentTypes, row.code)) return;
        if ((c.documents || []).some(d => d.documentType === row.code)) return;
        const ofType = orgDocGroups.filter(g => (g.common?.documentType || '').toUpperCase() === row.code.toUpperCase());
        const group = ofType.find(g => g.common?.isCurrent) || ofType[0];
        jobs.push({ customerId: c.id, code: row.code, label: row.label, group, file: group?.documents?.[0] });
      });
    });
    if (jobs.length === 0) return;
    setCustomers(prev => prev.map(c => {
      const mine = jobs.filter(j => j.customerId === c.id).map(j => j.code);
      return mine.length ? { ...c, _orgPrefillTried: [...(c._orgPrefillTried || []), ...mine] } : c;
    }));
    const api = tcOrgDocumentApi(orgDocsOrgId);
    jobs.filter(j => j.file?.filePath).forEach(j => {
      api.view(j.file.filePath)
        .then((res) => {
          const blob = res.data;
          const name = j.file.fileName || 'document';
          const file = new File([blob], name, { type: blob?.type || 'application/octet-stream' });
          const doc = {
            id: Date.now() + Math.random(),
            file,
            fileName: name,
            fileSize: file.size,
            documentType: j.code,
            documentTypeName: j.label,
            extracting: false,
            documentNumber: j.group.common?.documentNumber || null,
            issueDate: j.group.common?.issueDate || null,
            expiryDate: j.group.common?.expiryDate || null,
            _orgDocKey: j.file.filePath,
          };
          setCustomers(prev => prev.map(c => c.id === j.customerId
            && !(c.documents || []).some(d => d.documentType === j.code)
            ? { ...c, documents: [...c.documents, doc] } : c));
        })
        .catch((err) => {
          console.error(`[WO prefill] ${j.code}: the organization's file could not be fetched`, err);
          setError(prev => prev || `The customer's ${j.label || j.code} could not be attached - upload it on the row.`);
        });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers, orgDocsOrgId, orgDocGroups, documentTypes]);

  // Submit handler — validates, then creates.
  const handleCreateWorkOrder = async () => {
    if (customerType === 'organization' && !selectedOrganization) {
      setError('Please select an organization');
      return;
    }

    // Every applicant must have a name — a blank-name customer would otherwise be silently
    // dropped along with its services/documents.
    if (customers.some(c => !c.name.trim())) {
      setError('Please enter a name for every applicant');
      return;
    }
    const validCustomers = customers.filter(c => c.name.trim());

    // Employee mode: every applicant is a picked (or Quick-added) employee.
    if (employeeMode && validCustomers.some(c => !c.employee?.uid)) {
      setError('Select or add the employee for every applicant');
      return;
    }

    const hasServices = validCustomers.some(c => c.services.length > 0);
    if (!hasServices) {
      setError('Please add at least one service to any customer');
      return;
    }

    // Required custom fields must have a value (hard block). Fields are merged per customer,
    // so a field shared by several services is validated once against the customer's value.
    for (const customer of validCustomers) {
      for (const f of mergeCustomFields(customer)) {
        const val = customer.fieldValues?.[f.fieldId];
        if (f.required && (val == null || String(val).trim() === '')) {
          setError(`Please fill the required field "${f.label}" for ${customer.name}`);
          return;
        }
      }
    }

    // Required documents are merged per customer (deduped across services). Only docs marked
    // required are hard-blocked; optional ones can be uploaded but are never enforced.
    const missing = [];
    for (const customer of validCustomers) {
      const uploadedDocTypes = new Set((customer.documents || []).map(d => d.documentType || d.document_type));
      // Also count on-file docs from existing employee
      const onFileDocs = new Set((employeeMode ? [] : (customer._existingDocs || []))
        .filter(g => !isExpiredOnFile(g))
        .map(g => (g.common?.documentType || '').toUpperCase()));
      const requiredMissing = mergeRequiredDocs(customer).filter(r => r.required && !uploadedDocTypes.has(r.code) && !onFileDocs.has(r.code?.toUpperCase()));
      if (requiredMissing.length > 0) {
        missing.push({
          customerName: customer.name,
          serviceName: '',
          missingDocs: requiredMissing
        });
      }
    }
    if (missing.length > 0) {
      setMissingDocs(missing);
      setDocWarningOpen(true);
      return;
    }

    await proceedCreateWorkOrder();
  };

  // Sync each customer's uploaded documents to its picked employee (employees are picked or Quick-added on the page,
  // never created here). Files pre-filled from the employee came from Thaklis and are not sent back.
  const syncEmployeesAndDocuments = async (validCustomers, failedDocIds = new Set()) => {
    if (!isEmployeeTrackingActive()) {
      // Typing-center customers (and Thaklis-disabled TCs) get no employee/document tracking.
      return;
    }
    // A document whose WO (/documents) upload failed is never pushed to Thaklis either.
    const isSyncable = (d) => d.file && !d._fromEmployee && !failedDocIds.has(d.id)
      && !isOrganizationDocType(documentTypes, d.documentType);
    const trackable = validCustomers.filter(c => c.employee?.uid && (c.documents || []).some(isSyncable));
    if (trackable.length === 0) return;

    setSyncing(true);

    await Promise.all(trackable.map(async (customer) => {
      try {
        const uid = customer.employee.uid;
        const segment = customer.employee.segment || customerKey;
        // Group this customer's staged files by document type — files of the same
        // type are attached together in one bulk batch (all become the latest version).
        const groups = new Map();
        for (const doc of (customer.documents || [])) {
          if (!isSyncable(doc)) continue;
          if (!groups.has(doc.documentType)) groups.set(doc.documentType, []);
          groups.get(doc.documentType).push(doc);
        }
        await Promise.all(Array.from(groups.entries()).map(async ([documentType, docs]) => {
          const files = docs.map(d => d.file);
          // Shared metadata for the batch: first non-empty value across the group's files.
          const pick = (key) => {
            const hit = docs.find(d => d[key]);
            return hit ? hit[key] : null;
          };
          try {
            await ApiService.uploadEmployeeDocuments(uid, files, documentType, {
              documentNumber: pick('documentNumber'),
              issueDate: pick('issueDate'),
              expiryDate: pick('expiryDate')
            }, segment);
          } catch (docErr) {
            console.error(`Failed to sync ${documentType} documents for ${customer.name}:`, docErr);
          }
        }));
      } catch (custErr) {
        console.error(`Failed to sync employee for ${customer.name}:`, custErr);
        setError(prev => prev || `Some applicants could not be synced to Thaklis (${customer.name}).`);
      }
    }));
    setSyncing(false);
  };

  const proceedCreateWorkOrder = async () => {
    const validCustomers = customers.filter(c => c.name.trim());

    setCreating(true);
    setError(null);

    try {
      const { subtotal, taxTotal, grandTotal } = calculateGrandTotals();
      const typingCenterId = currentUser?.typingCenterId || currentUser?.typing_center_id;

      // Flatten all line items with customer reference
      const allLineItems = [];
      const allDocuments = [];
      // Tickets created in-place for Thaklis lines, keyed by the line's sort_order so we
      // can match them to the persisted line items (and their WO tasks) after create.
      const ticketsToLink = [];
      let sortOrder = 0;

      validCustomers.forEach((customer, custIndex) => {
        customer.services.forEach((item, itemIndex) => {
          const isValidUUID = item.service_id &&
            /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(item.service_id);

          const thisSort = sortOrder++;
          allLineItems.push({
            customer_id: customer.id,
            customer_name: customer.name,
            employee_uid: customer.employee?.uid || null,
            customer_id_number: customer.passportNumber?.trim() || null,
            customer_emirates_id: customer.emiratesId?.trim() || null,
            service_id: isValidUUID ? item.service_id : null,
            service_name: item.service_name,
            service_description: item.service_description || '',
            service_type: item.service_type || 'INTERNAL',
            quantity: item.quantity,
            unit_price: item.unit_price,
            service_charge: item.service_charge,
            govt_charge: item.govt_charge,
            tax_value: item.tax_value,
            tax_on_markup: item.tax_on_markup,
            line_total: item.line_total,
            original_line_total: item.original_line_total,
            markup_amount: item.markup_amount,
            is_custom_service: item.is_custom_service,
            fine_amount: item.fine_amount || 0,
            sort_order: thisSort,
            // Distribute the customer's shared field values to this line item's own service fields.
            field_values: (item.customFields || [])
              .map(f => ({ fieldId: f.fieldId, value: customer.fieldValues?.[f.fieldId] }))
              .filter(fv => fv.value != null && String(fv.value).trim() !== '')
          });

          if (!isEditMode && item.created_ticket?.id) {
            ticketsToLink.push({ sortOrder: thisSort, ticket: item.created_ticket });
          }

        });

        customer.documents.forEach(d => {
          allDocuments.push({
            customer_id: customer.id,
            customer_name: customer.name,
            document_type: d.documentType,
            file_name: d.fileName,
            file_size: d.fileSize
          });
        });
      });

      const workOrderData = {
        typing_center_id: typingCenterId,
        organization_id: customerType === 'organization' ? selectedOrganization?.id : null,
        organization_name: customerType === 'organization' ? selectedOrganization?.name : null,
        organization_code: customerType === 'organization' ? selectedOrganization?.code : null,
        mapped_organization_id: selectedMappedOrg?.id || null,
        mapped_organization_name: selectedMappedOrg?.name || null,
        customer_type: customerType,
        customers: validCustomers.map(c => ({
          id: c.id,
          name: c.name.trim(),
          passport_number: c.passportNumber?.trim() || null,
          emirates_id: c.emiratesId?.trim() || null
        })),
        customer_email: customerEmail,
        customer_phone: customerPhone,
        order_date: orderDate,
        expected_completion_date: expectedCompletionDate,
        subtotal,
        tax_total: taxTotal,
        grand_total: grandTotal,
        title: title.trim() || null,
        description,
        notes,
        documents: allDocuments,
        // Line items are only sent on create. In edit mode, services are
        // managed inline on the WO detail page (add/delete individually).
        ...(isEditMode ? {} : { line_items: allLineItems })
      };

      let response;
      if (isEditMode) {
        response = await ApiService.updateWorkOrder(workOrderId, workOrderData);
      } else {
        response = await ApiService.createWorkOrder(workOrderData);
      }

      const newWorkOrder = response?.data?.data || response?.data;
      const woId = newWorkOrder?.id || workOrderId;

      // Upload each document to the WO record (Managers S3). Track any that fail so we can skip the
      // Thaklis employee sync for them — a document that isn't on the WO must not be pushed to
      // Thaklis, so the two stores stay in step. Failed docs can be re-uploaded from the WO page.
      const failedDocIds = new Set();
      for (const customer of validCustomers) {
        for (const d of (customer.documents || [])) {
          if (!d.file) continue;
          if (d._orgDocKey) {
            // pre-filled from the customer's company documents: reuse that file, no second upload
            try {
              await ApiService.attachWorkOrderOrganizationDocument(woId, {
                s3Key: d._orgDocKey, documentType: d.documentTypeName || d.documentType, customerName: customer.name?.trim() || null,
                employeeUid: customer.employee?.uid || null,
              });
            } catch (attachErr) {
              console.error('Error attaching the company document to the work order:', attachErr);
              failedDocIds.add(d.id);
            }
            continue;
          }
          try {
            // Send the label (documentTypeName), not the code
            // Every document carries the applicant it was uploaded under (name and employee uid), company documents too
            await ApiService.uploadWorkOrderDocument(woId, d.file, '', d.documentTypeName || d.documentType, customer.name?.trim() || null,
              null, null, customer.employee?.uid || null, d.documentType || null);
          } catch (uploadErr) {
            console.error('Error uploading document to work order:', uploadErr);
            failedDocIds.add(d.id);
          }
        }
      }

      // Auto-link any tickets created in-place from Thaklis service lines. The WO tasks
      // are auto-created on the server, so we re-fetch the WO to map each ticket's line
      // (matched by sort_order) to its task, then call the same link-ticket API the manual
      // flow uses. Best-effort: a link failure does not roll back the WO.
      if (!isEditMode && ticketsToLink.length > 0) {
        try {
          const detailResp = await ApiService.getWorkOrderById(woId);
          const wo = detailResp?.data?.data || detailResp?.data;
          const woLineItems = wo?.lineItems || wo?.line_items || [];
          const woTasks = wo?.tasks || [];
          for (const { sortOrder: so, ticket } of ticketsToLink) {
            const li = woLineItems.find(l => Number(l.sort_order ?? l.sortOrder) === Number(so));
            if (!li) continue;
            const task = woTasks.find(t => String(t.lineItemId ?? t.line_item_id) === String(li.id));
            if (!task) continue;
            try {
              await ApiService.linkTaskToTicket(woId, task.id, {
                thaklis_ticket_id: ticket.id,
                thaklis_ticket_number: ticket.number,
                ticket_snapshot: ticket.snapshot || ticket
              });
            } catch (oneErr) {
              console.error(`Failed to auto-link ticket ${ticket.number} to task ${task.id}:`, oneErr);
            }
          }
        } catch (linkErr) {
          console.error('Failed to auto-link created tickets:', linkErr);
        }
      }

      // Sync applicants to Thaklis employees + attach their documents (create mode only), skipping
      // any document whose WO upload failed. Best-effort: Thaklis failures do not roll back the WO.
      // Skipped entirely when Thaklis features are disabled (Managers-S3 upload above still runs).
      if (!isEditMode && !thaklisDisabled) {
        try {
          await syncEmployeesAndDocuments(validCustomers, failedDocIds);
        } catch (syncErr) {
          console.error('Employee/document sync failed:', syncErr);
        }
      }

      if (failedDocIds.size > 0) {
        window.alert(`Work order created, but ${failedDocIds.size} document(s) failed to upload. Please re-upload them from the work order's Documents section.`);
      }

      setSuccess(isEditMode ? 'Work order updated successfully!' : 'Work order created successfully!');

      // Keep the blocking loader up until we navigate away, so the form can't be
      // re-submitted in the gap between the APIs finishing and the redirect.
      setTimeout(() => {
        navigate(`/work-orders/${woId}`);
      }, 1500);
    } catch (err) {
      console.error('Error creating/updating work order:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to save work order');
      setCreating(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED'
    }).format(amount || 0);
  };

  const currentCustomer = customers[activeTab];
  const currentCustomerTotals = currentCustomer ? calculateCustomerTotal(currentCustomer) : { subtotal: 0, taxTotal: 0, grandTotal: 0 };
  // Custom fields and required documents merged across the current customer's services (deduped).
  const mergedFields = currentCustomer ? mergeCustomFields(currentCustomer) : [];
  const mergedDocs = currentCustomer ? docRows(currentCustomer) : [];
  const mergedDocCodes = new Set(mergedDocs.map(d => d.code));
  // Types offered in the "add another document" dropdown: exclude what already has a slot or upload.
  const uploadedDocCodes = new Set((currentCustomer?.documents || []).map(d => d.documentType));
  const extraDropdownTypes = documentTypes.filter(dt => {
    const code = dt.code || dt.id;
    return !mergedDocCodes.has(code) && !uploadedDocCodes.has(code);
  });
  // Uploaded documents that don't belong to a required slot (ad-hoc extras).
  const extraUploadedDocs = (currentCustomer?.documents || []).filter(d => !mergedDocCodes.has(d.documentType));

  // One uploaded-document row — reused by required-doc slots and the extras list.
  const renderUploadedDoc = (doc) => (
    <Box key={doc.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
      <Typography noWrap sx={{ fontSize: fontSize.xs, color: 'text.secondary', flex: 1, minWidth: 0 }}>
        {doc.fileName}
        <Typography component="span" sx={{ fontSize: fontSize.xs, color: 'text.disabled', ml: 0.5 }}>
          · {formatFileSize(doc.fileSize)}
          {doc.documentNumber ? ` · ${doc.documentNumber}` : ''}
          {doc.expiryDate ? ` · Exp ${doc.expiryDate}` : ''}
        </Typography>
      </Typography>
      {doc.extracting && <CircularProgress size={12} />}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, minWidth: 180, justifyContent: 'flex-end' }}>
        <Chip label="Uploaded" size="small" color="success" variant="filled" sx={{ fontSize: '0.6rem', height: 18 }} />
        {doc.file && (
          <Button size="small" onClick={() => window.open(URL.createObjectURL(doc.file), '_blank')}
            sx={{ fontSize: fontSize.xs, textTransform: 'none', minWidth: 0, p: 0.5, color: 'text.secondary' }}>
            View
          </Button>
        )}
        <Button size="small" onClick={() => handleRemoveDocument(doc.id)}
          sx={{ fontSize: fontSize.xs, textTransform: 'none', minWidth: 0, p: 0.5, color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
          Remove
        </Button>
      </Box>
    </Box>
  );
  const grandTotals = calculateGrandTotals();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress sx={{ color: 'secondary.main' }} />
      </Box>
    );
  }

  return (
    <Box sx={{
      pb: isMobile ? 2 : 0,
      '& .MuiInputBase-root': { height: 36 },
      '& .MuiInputBase-multiline': { height: 'auto' },
      '& .MuiAutocomplete-inputRoot': { height: 36 },
      '& .MuiInputBase-input': { fontSize: '0.8125rem' },
      '& .MuiInputLabel-root': { fontSize: '0.8125rem' },
      '& .MuiToggleButton-root': { py: 0.5, fontSize: '0.75rem' },
    }}>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={1.5} mb={2}>
        <IconButton size="small" onClick={() => navigate('/work-orders')}>
          <BackIcon fontSize="small" />
        </IconButton>
        <Typography sx={{ fontSize: '1rem', fontWeight: 600 }}>
          {isEditMode ? 'Edit Work Order' : 'Create Work Order'}
        </Typography>
      </Box>

      {/* Errors surface as a toast so they're seen even when scrolled down the form. */}
      <Snackbar
        open={!!error}
        onClose={(e, reason) => { if (reason !== 'clickaway') setError(null); }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ maxWidth: 480 }}
      >
        <Alert severity="error" variant="filled" onClose={() => setError(null)} sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Full-page block while a document is extracting — the form must not be edited until it finishes. */}
      <Backdrop
        open={customers.some(c => (c.documents || []).some(d => d.extracting))}
        sx={{ color: '#fff', zIndex: (t) => t.zIndex.modal + 1 }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress color="inherit" />
          <Typography>Extracting document details… please wait</Typography>
        </Box>
      </Backdrop>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Left Column - Form */}
        <Grid item xs={12} lg={8}>
          {/* Customer & Organization — merged into one compact card */}
          <Paper sx={{ p: 2.5, mb: 2 }}>
            {/* Customer Type Toggle — only when Thaklis features enabled */}
            {!thaklisDisabled && (
              <Box sx={{ mb: 2 }}>
                <ToggleButtonGroup
                  value={customerType}
                  exclusive
                  onChange={(e, value) => value && handleCustomerTypeChange(value)}
                  fullWidth
                  size="small"
                >
                  <ToggleButton value="organization" sx={{ py: 0.5, fontSize: fontSize.xs, '&.Mui-selected': { bgcolor: 'action.selected', color: 'secondary.main' } }}>
                    Organization
                  </ToggleButton>
                  <ToggleButton value="walkin" sx={{ py: 0.5, fontSize: fontSize.xs, '&.Mui-selected': { bgcolor: 'action.selected', color: 'secondary.main' } }}>
                    Walk-in Customer
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}

            <Grid container spacing={1.5}>
              {/* Thaklis Organization — hidden when only 1 (auto-selected) */}
              {mappedOrganizations.length > 1 && (
                <Grid item xs={12}>
                  <Autocomplete ListboxProps={COMPACT_LISTBOX}
                    size="small"
                    options={mappedOrganizations}
                    getOptionLabel={(option) => `${option.name}${option.code ? ` (${option.code})` : ''}`}
                    value={selectedMappedOrg}
                    onChange={(e, value) => setSelectedMappedOrg(value)}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    loading={loadingMappedOrgs}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Thaklis Organization"
                        placeholder="Search..."
                        size="small"
                      />
                    )}
                  />
                </Grid>
              )}

              {/* Organization Selection */}
              {customerType === 'organization' && (
                <Grid item xs={12}>
                  <Autocomplete ListboxProps={COMPACT_LISTBOX}
                    size="small"
                    options={organizations}
                    getOptionLabel={(option) => `${option.name}${option.code ? ` (${option.code})` : ''}`}
                    isOptionEqualToValue={(o, v) => o?.id === v?.id}
                    value={selectedOrganization}
                    onChange={handleOrganizationChange}
                    onInputChange={onOrganizationInputChange}
                    filterOptions={organizationFilterOptions}
                    loadingText="Searching..."
                    noOptionsText="No organizations found"
                    loading={loadingOrgs}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Organization *"
                        placeholder="Search by name or TRN..."
                        size="small"
                        autoComplete="off"
                        inputProps={{ ...params.inputProps, autoComplete: 'new-password' }}
                      />
                    )}
                  />
                </Grid>
              )}

              {/* Contact — auto-filled from organization */}
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Email"
                  type="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  placeholder="contact@example.com"
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Phone"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  placeholder="+971 50 xxx xxxx"
                />
              </Grid>

              {/* Dates — same row as Email/Phone */}
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Order Date"
                  type="date"
                  value={orderDate}
                  onChange={(e) => setOrderDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Completion Date"
                  type="date"
                  value={expectedCompletionDate}
                  onChange={(e) => setExpectedCompletionDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Title + Notes — single row */}
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Visa renewal – Ahmed"
                  inputProps={{ maxLength: 50 }}
                />
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  size="small"
                  label="Notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Internal notes (optional)"
                  multiline
                  minRows={1}
                  maxRows={4}
                />
              </Grid>
              {/* Older work orders carry a separate description; keep it visible and editable. */}
              {isEditMode && description && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    multiline
                    minRows={1}
                    maxRows={4}
                  />
                </Grid>
              )}
            </Grid>
          </Paper>

          {/* Customer Tabs Section — create mode only. In edit mode the WO update edits header fields
              only; services are managed on the WO detail page and applicants/documents aren't edited here. */}
          {!isEditMode && (
          <Paper sx={{ mb: 3 }}>
            {/* Tab Header */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, pt: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant={isMobile ? 'scrollable' : 'standard'}
                scrollButtons="auto"
                sx={{
                  minHeight: 48,
                  '& .MuiTab-root': {
                    minHeight: 48,
                    textTransform: 'none',
                    fontSize: fontSize.sm,
                    fontWeight: 500
                  },
                  '& .Mui-selected': {
                    color: 'secondary.main'
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: 'secondary.main'
                  }
                }}
              >
                {customers.map((customer, index) => (
                  <Tab
                    key={customer.id}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {isCustomerComplete(customer) ? (
                          <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
                        ) : (
                          <PersonIcon sx={{ fontSize: 16 }} />
                        )}
                        <span>{customer.name || `Customer ${index + 1}`}</span>
                        {customer.services.length > 0 && (
                          <Chip
                            label={customer.services.length}
                            size="small"
                            sx={{ height: 18, fontSize: '0.65rem', bgcolor: 'primary.main', color: 'white' }}
                          />
                        )}
                      </Box>
                    }
                  />
                ))}
              </Tabs>
              <Button
                size="small"
                startIcon={<PersonAddIcon />}
                onClick={handleAddCustomer}
                sx={{ ml: 1, color: 'secondary.main', whiteSpace: 'nowrap' }}
              >
                {isMobile ? '' : 'Add'}
              </Button>
            </Box>

            {/* Tab Content — Order: Applicant → Services → Documents */}
            {currentCustomer && (
              <Box sx={{ p: 2 }}>
                {/* 1. Applicant Details — search existing or enter new */}
                <Box sx={{ mb: 1.5 }}>
                  <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600, mb: 1, color: 'secondary.main' }}>
                    Applicant
                  </Typography>
                  {/* Employee first (employee mode): pick one of the customer's employees or Quick add */}
                  {employeeMode && (
                    <Box sx={{ mb: currentCustomer.employee ? 1.5 : 0 }}>
                      <WorkOrderEmployeePicker
                        key={currentCustomer.id}
                        compact
                        customer={customerKey}
                        applicant={{
                          name: currentCustomer.name,
                          passportNumber: currentCustomer.passportNumber,
                          emiratesId: currentCustomer.emiratesId,
                        }}
                        employee={currentCustomer.employee}
                        onSelect={(emp) => selectEmployee(currentCustomer.id, emp)}
                        onClear={() => clearEmployee(currentCustomer.id)}
                        onError={setError}
                      />
                    </Box>
                  )}

                  {/* Manual entry fields — shown when: walk-in, no org selected, or user chose "Create new" */}
                  {(!employeeMode || currentCustomer.employee) && (
                    <>
                      <Grid container spacing={1.5}>
                        <Grid item xs={12} sm={4}>
                          <TextField fullWidth size="small" label="Full Name *"
                            value={currentCustomer.name}
                            onChange={(e) => handleCustomerChange(currentCustomer.id, 'name', e.target.value)} />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <TextField fullWidth size="small" label="Passport"
                            value={currentCustomer.passportNumber}
                            onChange={(e) => handleCustomerChange(currentCustomer.id, 'passportNumber', e.target.value)} />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <TextField fullWidth size="small" label="Emirates ID"
                            value={currentCustomer.emiratesId}
                            onChange={(e) => handleCustomerChange(currentCustomer.id, 'emiratesId', e.target.value)} />
                        </Grid>
                        <Grid item xs={12} sm={2}>
                          {customers.length > 1 && (
                            <Button fullWidth variant="outlined" color="error" size="small"
                              onClick={() => handleRemoveCustomer(currentCustomer.id)} sx={{ height: 36 }}>
                              <DeleteIcon />
                            </Button>
                          )}
                        </Grid>
                      </Grid>
                    </>
                  )}
                </Box>

                <Divider sx={{ my: 1.5 }} />

                {/* Employee mode: services, documents and fields stay disabled until an employee is selected */}
                <Box aria-disabled={employeeMode && !currentCustomer.employee}
                  sx={employeeMode && !currentCustomer.employee ? { opacity: 0.45, pointerEvents: 'none', userSelect: 'none' } : undefined}>

                {/* 2. Services — selection + details table */}
                {!isEditMode && (
                  <>
                    <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600, mb: 1, color: 'secondary.main' }}>
                      Services
                    </Typography>

                    {/* Service Selection Row */}
                    <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, bgcolor: 'action.hover', borderRadius: 1.5 }}>
                      {/* KAN-29: internal services on the first tab; Thaklis services on the second,
                          shown only when Thaklis services are enabled in Work Order Settings. */}
                      {showThaklisServices && (
                        <>
                          <Tabs
                            value={serviceTab}
                            onChange={(e, v) => setServiceTab(v)}
                            sx={{ mb: 1, minHeight: 36 }}
                          >
                            <Tab label="Internal Services" sx={{ minHeight: 36, py: 0 }} />
                            <Tab label="Thaklis Services" sx={{ minHeight: 36, py: 0 }} />
                          </Tabs>
                          {serviceTab === 1 && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                              These services are provided by Thaklis — adding them means you are planning to give this work to Thaklis.
                            </Alert>
                          )}
                        </>
                      )}
                      <ServicePicker
                        key={`${activeTab}-${serviceTab}-${customerType}-${selectedOrganization?.id || ''}`}
                        services={availableServices.filter(s => (showThaklisServices && serviceTab === 1)
                          ? s.type !== 'INTERNAL'
                          : s.type === 'INTERNAL')}
                        groupServices={availableServices}
                        groups={showThaklisServices && serviceTab === 1 ? [] : serviceGroups}
                        onAddService={handleAddServiceToCustomer}
                        onAddGroup={handleAddGroupToCustomer}
                      />
                    </Paper>

                    {/* Groups added to this applicant — remove a whole group in one go */}
                    {(() => {
                      const added = [...new Map(currentCustomer.services.filter(s => s.group_id)
                        .map(s => [s.group_id, s.group_name])).entries()];
                      return added.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, mb: 1 }}>
                          <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Groups added:</Typography>
                          {added.map(([gid, gname]) => (
                            <Chip key={gid} size="small" icon={<GroupChipIcon sx={{ fontSize: '0.9rem !important' }} />}
                              label={`${gname} · ${currentCustomer.services.filter(s => s.group_id === gid).length}`}
                              onDelete={() => handleRemoveGroupFromCustomer(gid)}
                              deleteIcon={<CloseIcon aria-label={`Remove the ${gname} group`} />}
                              sx={{ fontSize: '0.75rem', fontWeight: 600 }} />
                          ))}
                        </Box>
                      );
                    })()}

                    {/* Services Table */}
                    {currentCustomer.services.length === 0 ? (
                      <Typography sx={{ fontSize: fontSize.xs, color: 'text.disabled', textAlign: 'center', py: 1.5 }}>
                        No services added yet
                      </Typography>
                    ) : (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              {['Service', 'Service Fee', 'Govt Fee', 'Markup', 'Tax', 'Total', ''].map((h, i) => (
                                <TableCell key={i} sx={{ bgcolor: 'action.hover', fontWeight: 600, fontSize: '0.75rem', py: 0.5 }}
                                  align={i > 0 ? 'right' : 'left'} width={i === 6 ? 40 : undefined}>{h}</TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {currentCustomer.services.map((item) => {
                              const mkp = item.markup_amount || 0;
                              const taxOnMkp = item.tax_on_markup || 0;
                              return (
                              <TableRow hover key={item.id}>
                                <TableCell sx={{ py: 0.75 }}>
                                  <Typography sx={{ fontSize: '0.8125rem' }}>{item.service_name}</Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25, flexWrap: 'wrap' }}>
                                    {item.group_name && (
                                      <Chip label={item.group_name} size="small" variant="filled"
                                        sx={{ fontSize: '0.6rem', height: 16, bgcolor: 'action.selected' }} />
                                    )}
                                    {/* Only show type chip when Thaklis services are mixed in */}
                                    {showThaklisServices && (
                                      <Chip
                                        label={item.service_type}
                                        size="small"
                                        sx={{ fontSize: '0.6rem', height: 16 }}
                                        color={item.service_type === 'THAKLIS' ? 'primary' : 'default'}
                                      />
                                    )}
                                    {!thaklisDisabled && item.service_type === 'THAKLIS' && (
                                      item.created_ticket ? (
                                        <Chip
                                          icon={<CheckIcon sx={{ fontSize: '0.9rem !important' }} />}
                                          label={item.created_ticket.number || 'Ticket created'}
                                          size="small"
                                          color="success"
                                          onDelete={() => handleRemoveCreatedTicket(currentCustomer.id, item.id)}
                                          sx={{ fontSize: '0.65rem', height: 20, fontWeight: 600 }}
                                        />
                                      ) : canCreateTicket && (
                                        <Button
                                          size="small"
                                          variant="contained"
                                          color="warning"
                                          startIcon={<TicketIcon sx={{ fontSize: '0.95rem !important' }} />}
                                          onClick={() => handleOpenTicketDialog(item)}
                                          sx={{
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            fontSize: '0.68rem',
                                            py: 0.15,
                                            px: 1,
                                            minHeight: 0,
                                            lineHeight: 1.4,
                                            boxShadow: 2,
                                            '@keyframes ticketPulse': {
                                              '0%, 100%': { boxShadow: '0 0 0 0 rgba(237,108,2,0.55)' },
                                              '50%': { boxShadow: '0 0 0 5px rgba(237,108,2,0)' }
                                            },
                                            animation: 'ticketPulse 1.8s infinite'
                                          }}
                                        >
                                          Create Ticket
                                        </Button>
                                      )
                                    )}
                                  </Box>
                                </TableCell>
                                <TableCell align="right" sx={{ fontSize: '0.8125rem', py: 0.75 }}>{formatCurrency(item.service_charge)}</TableCell>
                                <TableCell align="right" sx={{ fontSize: '0.8125rem', py: 0.75, color: parseFloat(item.govt_charge) > 0 ? 'text.primary' : 'text.disabled' }}>
                                  {parseFloat(item.govt_charge) > 0 ? formatCurrency(item.govt_charge) : '-'}
                                </TableCell>
                                <TableCell align="right" sx={{ fontSize: '0.8125rem', py: 0.75, color: parseFloat(mkp) > 0 ? 'success.main' : 'text.disabled' }}>
                                  {parseFloat(mkp) > 0 ? formatCurrency(mkp) : '-'}
                                </TableCell>
                                <TableCell align="right" sx={{ fontSize: '0.8125rem', py: 0.75 }}>
                                  {formatCurrency(item.tax_value)}
                                  {taxOnMkp > 0 && <Typography component="span" sx={{ fontSize: '0.7rem', color: 'success.main' }}> (+{formatCurrency(taxOnMkp)})</Typography>}
                                </TableCell>
                                <TableCell align="right" sx={{ fontSize: '0.8125rem', fontWeight: 600, py: 0.75 }}>
                                  {formatCurrency(item.line_total)}
                                </TableCell>
                                <TableCell align="center" sx={{ py: 0.75 }}>
                                  <IconButton size="small" sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}
                                    onClick={() => handleRemoveServiceFromCustomer(item.id)}>
                                    <DeleteIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                              );
                            })}
                            {/* Subtotal Row */}
                            <TableRow>
                              <TableCell colSpan={5} align="right" sx={{ fontWeight: 600 }}>
                                Customer Subtotal:
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, color: 'secondary.main' }}>
                                {formatCurrency(currentCustomerTotals.grandTotal)}
                              </TableCell>
                              <TableCell />
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </>
                )}

                <Divider sx={{ my: 1.5 }} />

                {/* Documents for this customer — required docs merged across all selected services
                    (deduped), each with its own upload slot, then an optional "add another". */}
                <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600, mb: 1, color: 'secondary.main' }}>
                  Documents
                </Typography>

                {/* Required / expected document slots (one per document type, deduped) */}
                {mergedDocs.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    {mergedDocs.map((reqDoc) => {
                      const docsForType = currentCustomer.documents.filter(d => d.documentType === reqDoc.code);
                      const uploaded = docsForType.length > 0;
                      const existingGroups = currentCustomer._existingDocs || [];
                      const onFileAny = existingGroups.find((g) =>
                        g.common?.documentType?.toUpperCase() === reqDoc.code?.toUpperCase());
                      // Employee mode: the on-file copy is pre-filled as an attached file (with Remove), so the row
                      // shows that file, never the Thaklis copy.
                      const onFile = employeeMode ? null : onFileAny;
                      const onFileDoc = onFile?.documents?.[0];
                      const onFileExpired = isExpiredOnFile(onFile);
                      const dropProps = {
                        tabIndex: 0,
                        onPaste: (e) => handleAddDroppedFiles(e, { code: reqDoc.code, label: reqDoc.label }),
                        onDrop: (e) => { setDragTarget(null); handleAddDroppedFiles(e, { code: reqDoc.code, label: reqDoc.label }); },
                        onDragOver: (e) => e.preventDefault(),
                        onDragEnter: (e) => { e.preventDefault(); setDragTarget(reqDoc.code); },
                        onDragLeave: () => setDragTarget(null),
                      };
                      // Expiry status for on-file docs
                      let expiryColor = 'success';
                      const docEmoji = getDocIcon(reqDoc.code);
                      if (onFile?.common?.expiryDate) {
                        const exp = new Date(onFile.common.expiryDate);
                        const now = new Date();
                        const sixMonths = new Date();
                        sixMonths.setMonth(sixMonths.getMonth() + 6);
                        if (exp < now) expiryColor = 'error';
                        else if (exp < sixMonths) expiryColor = 'warning';
                      }

                      return (
                        <Box
                          key={reqDoc.code}
                          {...dropProps}
                          title="Drop a file here, or click and paste a copied image (Ctrl/⌘+V)"
                          sx={{
                            display: 'flex', alignItems: 'center', gap: 1, py: 0.75, px: 1,
                            borderBottom: '1px solid', borderColor: 'divider',
                            '&:last-child': { borderBottom: 0 },
                            outline: dragTarget === reqDoc.code ? '2px dashed' : 'none',
                            outlineColor: 'primary.main', outlineOffset: -2,
                            bgcolor: dragTarget === reqDoc.code ? 'action.hover' : 'transparent',
                            '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 },
                          }}
                        >
                          {/* Icon */}
                          <Typography component="span" sx={{ fontSize: 16, lineHeight: 1, flexShrink: 0 }}>
                            {docEmoji}
                          </Typography>

                          {/* Label */}
                          <Typography noWrap sx={{ fontSize: fontSize.xs, fontWeight: 500, width: 140, flexShrink: 0 }}>
                            {reqDoc.label}
                            {reqDoc.required && (!onFile || onFileExpired) && !uploaded && <Box component="span" sx={{ color: 'error.main' }}> *</Box>}
                          </Typography>

                          {/* Status / file info */}
                          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                            {onFile && !uploaded && (
                              <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }} noWrap>
                                {onFileDoc?.fileName || 'On file'}
                                {onFile.common?.expiryDate && ` · Exp ${new Date(onFile.common.expiryDate).toLocaleDateString()}`}
                              </Typography>
                            )}
                            {uploaded && (
                              <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                                {docsForType.map((doc) => (
                                  <Box key={doc.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                                    <Typography noWrap sx={{ fontSize: fontSize.xs, color: 'text.secondary', minWidth: 0 }}>
                                      {doc.fileName}
                                      <Typography component="span" sx={{ fontSize: fontSize.xs, color: 'text.disabled', ml: 0.5 }}>
                                        · {formatFileSize(doc.fileSize)}
                                        {doc.expiryDate ? ` · Exp ${doc.expiryDate}` : ''}
                                      </Typography>
                                    </Typography>
                                    {doc.file && (
                                      <Button size="small" onClick={() => window.open(URL.createObjectURL(doc.file), '_blank')}
                                        sx={{ fontSize: fontSize.xs, textTransform: 'none', minWidth: 0, p: 0.25, color: 'text.secondary', flexShrink: 0 }}>
                                        View
                                      </Button>
                                    )}
                                    <Button size="small" onClick={() => handleRemoveDocument(doc.id)}
                                      sx={{ fontSize: fontSize.xs, textTransform: 'none', minWidth: 0, p: 0.25, color: 'text.disabled', flexShrink: 0, '&:hover': { color: 'error.main' } }}>
                                      Remove
                                    </Button>
                                  </Box>
                                ))}
                              </Box>
                            )}
                          </Box>

                          {/* Action — fixed width right section */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, minWidth: 180, justifyContent: 'flex-end' }}>
                            {uploaded ? (
                              <>
                                <Chip
                                  label={docsForType.length > 1 ? `${docsForType.length} uploaded` : 'Uploaded'}
                                  size="small" color="success" variant="filled" sx={{ fontSize: '0.6rem', height: 18 }}
                                />
                                <Button size="small" component="label"
                                  sx={{ fontSize: fontSize.xs, textTransform: 'none', minWidth: 0, px: 1 }}>
                                  Add file
                                  <input type="file" hidden multiple accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                    onChange={(e) => handleFileUpload(e, { code: reqDoc.code, label: reqDoc.label })} />
                                </Button>
                              </>
                            ) : (
                              <>
                                {onFile && (
                                  <Chip label={expiryColor === 'error' ? 'Expired' : expiryColor === 'warning' ? 'Expiring' : 'On file'}
                                    size="small" color={expiryColor} variant="filled"
                                    sx={{ fontSize: '0.6rem', height: 18 }} />
                                )}
                                <IconButton size="small" title="Capture from screen"
                                  onClick={() => setCaptureTarget({ code: reqDoc.code, label: reqDoc.label })}
                                  sx={{ color: 'text.secondary' }}>
                                  <CaptureIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                                <Button size="small" component="label"
                                  sx={{ fontSize: fontSize.xs, textTransform: 'none', minWidth: 0, px: 1 }}>
                                  {onFile ? 'Replace' : 'Upload'}
                                  <input type="file" hidden multiple accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                    onChange={(e) => handleFileUpload(e, { code: reqDoc.code, label: reqDoc.label })} />
                                </Button>
                              </>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}

                {/* Add another document — types not already covered by a slot or an upload above */}
                {/* Add another document — compact single line */}
                {extraDropdownTypes.length > 0 && (
                  <Box
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1, py: 0.75, px: 1, borderBottom: '1px solid', borderColor: 'divider',
                    }}
                  >
                    <Typography component="span" sx={{ fontSize: 16, lineHeight: 1, flexShrink: 0 }}>➕</Typography>
                    <FormControl size="small" sx={{ minWidth: 180, flex: 1 }}>
                      <InputLabel sx={{ fontSize: fontSize.xs }}>Add document...</InputLabel>
                      <Select
                        value={selectedDocType}
                        label="Add document..."
                        onChange={(e) => addExtraDocType(e.target.value)}
                        sx={{ fontSize: fontSize.xs }}
                        MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
                      >
                        {(() => {
                          const primaryDocs = extraDropdownTypes.filter(isPrimaryDocType);
                          const secondaryDocs = extraDropdownTypes.filter(dt => !isPrimaryDocType(dt));
                          const items = [];
                          if (primaryDocs.length > 0) {
                            items.push(<MenuItem key="_primary" disabled sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'primary.main', opacity: '1 !important', py: 0.25 }}>PRIMARY</MenuItem>);
                            primaryDocs.forEach(dt => items.push(
                              <MenuItem key={dt.code || dt.id} value={dt.code || dt.id} sx={{ fontSize: '0.8rem' }}>
                                {getDocIcon(dt.code)} {dt.label || dt.name || dt.code}
                              </MenuItem>
                            ));
                          }
                          if (secondaryDocs.length > 0) {
                            items.push(<MenuItem key="_secondary" disabled sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary', opacity: '1 !important', py: 0.25, borderTop: '1px solid', borderColor: 'divider' }}>SECONDARY</MenuItem>);
                            secondaryDocs.forEach(dt => items.push(
                              <MenuItem key={dt.code || dt.id} value={dt.code || dt.id} sx={{ fontSize: '0.8rem' }}>
                                {getDocIcon(dt.code)} {dt.label || dt.name || dt.code}
                              </MenuItem>
                            ));
                          }
                          if (items.length === 0) {
                            extraDropdownTypes.forEach(dt => items.push(
                              <MenuItem key={dt.code || dt.id} value={dt.code || dt.id} sx={{ fontSize: '0.8rem' }}>
                                {getDocIcon(dt.code)} {dt.label || dt.name || dt.code}
                              </MenuItem>
                            ));
                          }
                          return items;
                        })()}
                      </Select>
                    </FormControl>
                    <Typography sx={{ fontSize: '0.6875rem', color: 'text.disabled', whiteSpace: 'nowrap', flexShrink: 0 }}>
                      adds it to the list above
                    </Typography>
                  </Box>
                )}

                {/* Uploaded documents that don't map to a required slot */}
                {extraUploadedDocs.length > 0 && (
                  <List dense>
                    {extraUploadedDocs.map(renderUploadedDoc)}
                  </List>
                )}

                {mergedDocs.length === 0 && extraUploadedDocs.length === 0 && extraDropdownTypes.length === 0 && (
                  <Typography sx={{ fontSize: fontSize.sm, color: 'text.secondary', textAlign: 'center', py: 2 }}>
                    No documents uploaded yet
                  </Typography>
                )}

                {/* Service Information — custom fields after documents */}
                {mergedFields.length > 0 && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600, mb: 1, color: 'secondary.main' }}>
                      Additional Details
                    </Typography>
                    <Grid container spacing={1.5}>
                      {mergedFields.map((field) => {
                        const val = currentCustomer.fieldValues?.[field.fieldId] || '';
                        const label = field.label + (field.required ? ' *' : '');
                        const isWide = field.fieldType === 'TEXTAREA';
                        return (
                          <Grid item xs={6} sm={isWide ? 6 : 3} key={field.fieldId}>
                            {field.fieldType === 'SELECT' ? (
                              <TextField
                                select fullWidth size="small"
                                label={label}
                                value={val}
                                onChange={(e) => handleFieldValueChange(field.fieldId, e.target.value)}
                              >
                                <MenuItem value=""><em>—</em></MenuItem>
                                {(field.options || []).map((opt, oi) => (
                                  <MenuItem key={oi} value={typeof opt === 'string' ? opt : (opt.value || '')} sx={{ fontSize: '0.8125rem' }}>
                                    {typeof opt === 'string' ? opt : (opt.label || opt.value || '')}
                                  </MenuItem>
                                ))}
                              </TextField>
                            ) : field.fieldType === 'DATE' ? (
                              <TextField
                                fullWidth size="small" type="date"
                                label={label}
                                value={val}
                                onChange={(e) => handleFieldValueChange(field.fieldId, e.target.value)}
                                InputLabelProps={{ shrink: true }}
                              />
                            ) : (
                              <TextField
                                fullWidth size="small"
                                type={field.fieldType === 'NUMBER' ? 'number' : 'text'}
                                label={label}
                                value={val}
                                onChange={(e) => handleFieldValueChange(field.fieldId, e.target.value)}
                                placeholder={field.placeholder || ''}
                                multiline={isWide}
                                maxRows={isWide ? 4 : undefined}
                              />
                            )}
                          </Grid>
                        );
                      })}
                    </Grid>
                  </>
                )}
                </Box>
              </Box>
            )}
          </Paper>
          )}

        </Grid>

        {/* Right Column - Summary */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ position: 'sticky', top: 20 }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, cursor: 'pointer' }}
                onClick={() => setSummaryExpanded(!summaryExpanded)}
              >
                <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600, color: 'secondary.main' }}>
                  Order Summary
                </Typography>
                <IconButton size="small">
                  {summaryExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>

              <Collapse in={summaryExpanded}>
                {/* Customer Summaries */}
                {customers.filter(c => c.name.trim()).map((customer, index) => {
                  const totals = calculateCustomerTotal(customer);
                  const complete = isCustomerComplete(customer);
                  return (
                    <Paper
                      key={customer.id}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        mb: 1.5,
                        borderColor: complete ? 'success.light' : 'divider',
                        bgcolor: activeTab === index ? 'action.selected' : 'transparent',
                        cursor: 'pointer'
                      }}
                      onClick={() => setActiveTab(index)}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {complete ? (
                            <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
                          ) : (
                            <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          )}
                          <Typography sx={{ fontSize: fontSize.sm, fontWeight: 500 }}>
                            {customer.name}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600, color: 'secondary.main' }}>
                          {formatCurrency(totals.grandTotal)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                        <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>
                          {customer.services.length} service{customer.services.length !== 1 ? 's' : ''}
                        </Typography>
                        <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>
                          {customer.documents.length + (customer._existingDocs?.length || 0)} doc{(customer.documents.length + (customer._existingDocs?.length || 0)) !== 1 ? 's' : ''}
                        </Typography>
                      </Box>
                    </Paper>
                  );
                })}

                {customers.filter(c => c.name.trim()).length === 0 && (
                  <Typography sx={{ fontSize: fontSize.sm, color: 'text.secondary', textAlign: 'center', py: 2 }}>
                    No customers added yet
                  </Typography>
                )}

                <Divider sx={{ my: 1 }} />

                {/* Counts + Totals compact */}
                {[
                  ['Customers', customers.filter(c => c.name.trim()).length],
                  ['Services', customers.reduce((sum, c) => sum + c.services.length, 0)],
                  ['Documents', customers.reduce((sum, c) => sum + c.documents.length + (c._existingDocs?.length || 0), 0)],
                ].map(([label, val]) => (
                  <Box key={label} display="flex" justifyContent="space-between" mb={0.5}>
                    <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>{label}</Typography>
                    <Typography sx={{ fontSize: fontSize.xs, fontWeight: 500 }}>{val}</Typography>
                  </Box>
                ))}

                <Divider sx={{ my: 1 }} />

                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>Subtotal</Typography>
                  <Typography sx={{ fontSize: fontSize.xs }}>{formatCurrency(grandTotals.subtotal)}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>VAT (5%)</Typography>
                  <Typography sx={{ fontSize: fontSize.xs }}>{formatCurrency(grandTotals.taxTotal)}</Typography>
                </Box>

                <Divider sx={{ my: 1 }} />

                <Box display="flex" justifyContent="space-between">
                  <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600 }}>Grand Total</Typography>
                  <Typography sx={{ fontWeight: 700, color: 'secondary.main', fontSize: '1.1rem' }}>
                    {formatCurrency(grandTotals.grandTotal)}
                  </Typography>
                </Box>
              </Collapse>

              <Button
                fullWidth
                variant="contained"
                size="small"
                startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <SaveIcon sx={{ fontSize: 16 }} />}
                onClick={handleCreateWorkOrder}
                disabled={creating || (!isEditMode && customers.every(c => c.services.length === 0))}
                sx={{ mt: 1.5, py: 1, fontSize: fontSize.xs }}
              >
                {creating ? (syncing ? 'Syncing documents...' : 'Saving...') : (isEditMode ? 'Update Work Order' : 'Create Work Order')}
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Organization-change confirmation — warns before clearing entered data */}
      <Dialog open={orgChangeConfirmOpen} onClose={cancelOrgChange} maxWidth="sm" fullWidth>
        <DialogTitle>{pendingCustomerType ? 'Change customer type?' : 'Change organization?'}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: fontSize.sm }}>
            {pendingCustomerType ? (
              <>
                Available services and prices differ between walk-in and organization customers.
                Switching to <strong>{pendingCustomerType === 'walkin' ? 'walk-in' : 'organization'}</strong> will
                clear all customers, added services, uploaded documents, and entered details for this work order.
                Continue?
              </>
            ) : (
              <>
                Prices, field defaults, and available services are specific to each organization.
                Switching to <strong>{pendingOrgChange?.name || 'no organization'}</strong> will clear all
                customers, added services, uploaded documents, and entered details for this work order.
                Continue?
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={cancelOrgChange} color="inherit">Keep Current</Button>
          <Button onClick={confirmOrgChange} variant="contained" color="error">Change &amp; Clear</Button>
        </DialogActions>
      </Dialog>

      {/* Missing document requirements warning */}
      <Dialog open={docWarningOpen} onClose={() => setDocWarningOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>Required Documents Missing</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.8rem', mb: 2 }}>
            The following required documents must be uploaded before this work order can be created.
          </Typography>
          {missingDocs.map((item, idx) => (
            <Box key={idx} sx={{ mb: 1.5, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{item.customerName}</Typography>
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{item.serviceName}</Typography>
              {item.missingDocs.map((doc, i) => (
                <Typography key={i} sx={{ fontSize: '0.75rem', color: 'error.main', pl: 1 }}>
                  • {doc.label}
                </Typography>
              ))}
            </Box>
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDocWarningOpen(false)} variant="contained" color="secondary">Go Back</Button>
        </DialogActions>
      </Dialog>

      {/* Detected applicant differs from the customer on this tab */}
      <Dialog
        open={pendingMismatches.length > 0}
        onClose={handleKeepSameCustomer}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Different applicant detected</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: fontSize.sm }}>
            The uploaded document belongs to{' '}
            <strong>{pendingMismatches[0]?.extracted?.fullName || 'a different person'}</strong>, but
            this tab is for <strong>{pendingMismatches[0]?.existingName}</strong>.
          </DialogContentText>
          <DialogContentText sx={{ fontSize: fontSize.sm, mt: 1.5 }}>
            Do you want to keep this document with <strong>{pendingMismatches[0]?.existingName}</strong>,
            or create a new customer for{' '}
            <strong>{pendingMismatches[0]?.extracted?.fullName || 'the detected applicant'}</strong> and
            move the document there?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleMoveToNewCustomer} color="inherit" size="small" sx={{ textTransform: 'none' }}>
            Create new customer
          </Button>
          <Button onClick={handleKeepSameCustomer} variant="contained" size="small" sx={{ textTransform: 'none' }}>
            Keep with {pendingMismatches[0]?.existingName || 'current customer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Blocking full-page loader — stays up for the whole create chain
          (work order → document uploads → Thaklis tracking) until the redirect. */}
      <Backdrop
        open={creating}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 1, color: '#fff' }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* In-place ticket creation for a Thaklis service line */}
      <EmbeddedTicketDialog
        open={ticketDialog.open}
        onClose={handleCloseTicketDialog}
        mappingId={selectedMappedOrg?.mappingId}
        serviceId={ticketDialog.serviceItem?.service_id}
        serviceName={ticketDialog.serviceItem?.service_name}
        orgName={selectedMappedOrg?.name}
        prefillName={ticketDialog.prefillName}
        onCreated={handleTicketCreated}
      />
      {/* Screen Capture Dialog */}
      <CaptureDialog
        open={!!captureTarget}
        onClose={() => setCaptureTarget(null)}
        documentType={captureTarget?.label}
        onCapture={(file) => {
          if (captureTarget && currentCustomer) {
            // Name: username_doctype_date.jpg
            const nameSlug = (currentCustomer.name || 'unknown').replace(/\s+/g, '-').toUpperCase();
            const typeSlug = (captureTarget.label || captureTarget.code).replace(/\s+/g, '-').toLowerCase();
            const dateStr = new Date().toISOString().slice(0, 10);
            const fileName = `${nameSlug}_${typeSlug}_${dateStr}.jpg`;
            const renamedFile = new File([file], fileName, { type: file.type });

            const updated = [...customers];
            const idx = updated.findIndex((c) => c.id === currentCustomer.id);
            if (idx >= 0) {
              updated[idx] = {
                ...updated[idx],
                documents: [...updated[idx].documents, {
                  id: Date.now(),
                  documentType: captureTarget.code,
                  documentLabel: captureTarget.label,
                  file: renamedFile,
                  fileName,
                  fileSize: renamedFile.size,
                }],
              };
              setCustomers(updated);
            }
          }
        }}
      />
    </Box>
  );
};

export default CreateWorkOrder;
